import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { NumberField, CheckboxField, SelectField } from '../fields'
import { Box, Row, Col, Gap } from '..'
import { SelectOption } from '../fields/components/SelectComponents'
import { getIsFrameTypeDisabled } from '../../../common/catalogExtensions'
import { getIsPolishDisabledForFrameType } from '../../../common/orders/utils'
// others
import {
  frameTypes,
  bevelPositions,
  orderTypes,
  appTypeConfig,
} from '../../../common/config'

// This is mostly duplicate of OrderStep2Form.js FrameTypeBox
// We could use this component in OrderStep2Form.js,
// but this is used only for jai-kudos EDGING_WITHOUT_SHAPE
// and it might require some changes in future, that won't be compatible with OrderStep2Form.js use
// might be refactored later, when we are sure that it will be compatible
const FrameTypeBox = ({
  fields,
  viewer,
  currentLensR,
  currentLensL,
  catalogExtensions,
}) => {
  const { t } = useTranslation()
  const values = fields.$values()

  // if undefined, all is enabled
  const enabledFrameTypes = appTypeConfig.getFrameTypes?.()
  const frameTypeOptions = [
    { text: t('metallic'), value: frameTypes.METAL },
    { text: t('plastic'), value: frameTypes.PLASTIC },
    { text: t('titanium'), value: frameTypes.SPECIAL },
    { text: t('silon'), value: frameTypes.NYLOR },
    { text: t('drilled'), value: frameTypes.DRILLED },
    { text: t('silon + drilled'), value: frameTypes.GROOVEDDRILLED },
    { text: t('optyl'), value: frameTypes.OPTYL },
  ]
    .filter(
      ({ value }) => !enabledFrameTypes || enabledFrameTypes.includes(value),
    )
    .filter(
      (
        { value }, // check catalog extensions
      ) =>
        !getIsFrameTypeDisabled({
          frameType: value,
          lensR: currentLensR,
          lensL: currentLensL,
          catalogExtensions,
          orderFieldValues: values,
        }),
    )

  const grooveAndBevelOrderTypes = [
    orderTypes.EDGING,
    orderTypes.EDGING_COMPLETION,
    orderTypes.EDGING_WITHOUT_SHAPE,
  ]
  if (appTypeConfig.isSpectacleShapeModificationEnabled) {
    grooveAndBevelOrderTypes.push(orderTypes.WITH_FRAME)
  }

  let currentOrderType = values.orderType
  if (values.isEdgingWithoutShape)
    currentOrderType = orderTypes.EDGING_WITHOUT_SHAPE

  const isPolishDisabledForFrameType = getIsPolishDisabledForFrameType(
    fields.frameType.value,
  )

  return (
    <Box>
      <Box.Header>
        <Box.Title>
          {t('frame type')},{' '}
          {[frameTypes.NYLOR, frameTypes.GROOVEDDRILLED].includes(
            fields.frameType.value,
          )
            ? t('groove')
            : t('bevel')}
        </Box.Title>
      </Box.Header>
      <Box.Content>
        <Row grow="1" alignItems="flex-end">
          <Col width="20rem">
            <SelectField
              disabled={fields.frame.value}
              label={t('frame type')}
              required={
                [orderTypes.EDGING, orderTypes.EDGING_COMPLETION].includes(
                  values.orderType,
                ) || values.isEdgingWithoutShape
              }
              options={frameTypeOptions}
              {...fields.frameType}
              onChange={e => {
                if (appTypeConfig.step2AutoPolish) {
                  if (
                    [
                      frameTypes.DRILLED,
                      frameTypes.GROOVEDDRILLED,
                      frameTypes.NYLOR,
                    ].includes(e.value)
                  ) {
                    fields.$setValue('polish', true)
                  } else {
                    fields.$setValue('polish', false)
                  }
                }
                fields.frameType.onChange(e)
              }}
            />
          </Col>
          <Col>
            {values.orderType !== orderTypes.WITH_SHAPE && (
              <>
                <CheckboxField
                  disabled={
                    (values.orderType === orderTypes.WITH_FRAME &&
                      !appTypeConfig.isSpectaclesPolishEnabled &&
                      !viewer.group.isSpectaclesPolishEnabled) ||
                    isPolishDisabledForFrameType
                  }
                  label={t('polish')}
                  {...fields.polish}
                  value={
                    isPolishDisabledForFrameType ? false : fields.polish.value
                  }
                />
                <Gap gap="0.4rem" />
              </>
            )}
          </Col>
        </Row>
        {[frameTypes.NYLOR, frameTypes.GROOVEDDRILLED].includes(
          fields.frameType.value,
        ) &&
          grooveAndBevelOrderTypes.includes(currentOrderType) && (
            <Row grow="1">
              <Col>
                <NumberField
                  name="grooveWidth"
                  label={t('grooveWidth')}
                  {...fields.grooveWidth}
                />
              </Col>
              <Col>
                <NumberField
                  name="grooveDepth"
                  label={t('grooveDepth')}
                  {...fields.grooveDepth}
                />
              </Col>
            </Row>
          )}
        {grooveAndBevelOrderTypes.includes(currentOrderType) && (
          <Row grow="1" alignItems="flex-end">
            {values.frameType !== frameTypes.DRILLED && (
              <>
                <Col width="20rem">
                  <SelectField
                    label={
                      [frameTypes.NYLOR, frameTypes.GROOVEDDRILLED].includes(
                        fields.frameType.value,
                      )
                        ? t('groove position')
                        : t('bevel position')
                    }
                    options={appTypeConfig
                      .getBevelPositions()
                      .map(bevelPositionKey => ({
                        value: bevelPositionKey,
                        text: t(bevelPositionKey),
                      }))}
                    components={{ Option: SelectOption }}
                    {...fields.bevelPosition}
                  />
                </Col>
                {[
                  bevelPositions.FRONT,
                  bevelPositions.BACK,
                  bevelPositions.PERCENT,
                  bevelPositions.FRAMECURVE,
                ].includes(fields.bevelPosition.value) && (
                  <Col>
                    <NumberField
                      name="bevel-position-specification"
                      label={t('specification')}
                      {...fields.bevelModifier}
                      after={
                        fields.bevelPosition.value === bevelPositions.PERCENT
                          ? '%'
                          : 'mm'
                      }
                    />
                  </Col>
                )}
              </>
            )}
            {appTypeConfig.isPinBevelEnabled && 
              <CheckboxField label={t('Pin bevel')} {...fields.pinBevel} />
            }
          </Row>
        )}
      </Box.Content>
    </Box>
  )
}

const enhance = compose(
  connect(
    state => ({
      currentLensR: state.catalog.currentLensR,
      currentLensL: state.catalog.currentLensL,
      viewer: state.app.viewer,
      catalogExtensions: state.catalog.catalogExtensions,
    }),
    {},
  ),
)

export default enhance(FrameTypeBox)
